'use client';

import * as React from 'react';
import type { DateSelectArg, EventClickArg, EventDropArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import type { EventResizeDoneArg } from '@fullcalendar/interaction';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import Calendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import timelinePlugin from '@fullcalendar/timeline';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { CaretLeft as CaretLeftIcon } from '@phosphor-icons/react/dist/ssr/CaretLeft';
import { CaretRight as CaretRightIcon } from '@phosphor-icons/react/dist/ssr/CaretRight';
import { useNavigate } from 'react-router-dom';

import { paths } from '@/paths';
import { useDialog } from '@/hooks/use-dialog';
import { useUser } from '@/hooks/v2/use-user';
import { hasCalendarEditPermissions } from '@/utils/calendar-permissions';

import { CalendarContext } from './calendar-context';
import { EventContent } from './event-content';
import { EventDialog } from './event-dialog';
import { Toolbar } from './toolbar';
import type { ViewMode } from './types';
import type { ViewEventProps } from './view-event-dialog';
import { ViewEventDialog } from './view-event-dialog';
import { AccountRole } from '@/types/v2/account-role';
import { ACCOUNT_ROLES } from '@/constants/account-roles';
import { COMMUNITY_ROLES } from '@/constants/community-roles';

interface CreateDialogData {
  range?: { start: Date; end: Date };
}

const plugins = [dayGridPlugin, interactionPlugin, listPlugin, timeGridPlugin, timelinePlugin];

export interface CalendarViewProps {
  view?: ViewMode;
}

export function CalendarView({ view = 'dayGridMonth' }: CalendarViewProps): React.JSX.Element {
  const { events, currentEventId, setCurrentEventId, createEvent, deleteEvent, updateEvent, isLoading, isError } =
    React.useContext(CalendarContext);

  const { user, community, account } = useUser();
  const navigate = useNavigate();

  const calendarRef = React.useRef<Calendar | null>(null);

  const [date, setDate] = React.useState<Date>(new Date());

  const createDialog = useDialog<CreateDialogData>();

  const viewEventDialog = useDialog<ViewEventProps>();

  const currentEvent = currentEventId ? events.get(currentEventId) : undefined;

  // Check if user has edit permissions
  const hasEditPermissions = React.useMemo(() => {
    if (!user) return false;

    // Check account roles
    const accountRoles = account?.roles || [];
    const hasAccountRole = accountRoles.some((role: string) => 
      role === ACCOUNT_ROLES.ACCOUNT_OWNER || 
      role === ACCOUNT_ROLES.ACCOUNT_ADMIN ||
      role === ACCOUNT_ROLES.ACCOUNT_MANAGER
    );

    if (hasAccountRole) return true;

    // Check community roles
    const communityRoles = community?.roles || [];
    const hasCommunityRole = communityRoles.some(role => 
      role === COMMUNITY_ROLES.COMMUNITY_MANAGER
    );

    return hasCommunityRole;
  }, [user, account, community]);

  const handleViewChange = React.useCallback(
    (value: ViewMode) => {
      const calendarApi = calendarRef.current?.getApi();

      if (calendarApi) {
        calendarApi.changeView(value);
      }

      navigate(`/calendar-events?view=${value}`);
    },
    [navigate]
  );

  const handleDateToday = React.useCallback(() => {
    const calendarApi = calendarRef.current?.getApi();

    if (calendarApi) {
      calendarApi.today();
      setDate(calendarApi.getDate());
    }
  }, []);

  const handleDatePrev = React.useCallback(() => {
    const calendarApi = calendarRef.current?.getApi();

    if (calendarApi) {
      calendarApi.prev();
      setDate(calendarApi.getDate());
    }
  }, []);

  const handleDateNext = React.useCallback(() => {
    const calendarApi = calendarRef.current?.getApi();

    if (calendarApi) {
      calendarApi.next();
      setDate(calendarApi.getDate());
    }
  }, []);

  const handleAdd = React.useCallback(() => {
    if (!hasEditPermissions) {
      // For non-admin users, show a message or redirect
      return;
    }
    createDialog.handleOpen();
  }, [createDialog, hasEditPermissions]);

  const handleRangeSelect = React.useCallback(
    (arg: DateSelectArg) => {
      if (!hasEditPermissions) {
        // For non-admin users, don't allow range selection
        return;
      }

      const calendarApi = calendarRef.current?.getApi();

      if (calendarApi) {
        calendarApi.unselect();
      }

      createDialog.handleOpen({ range: { start: arg.start, end: arg.end } });
    },
    [createDialog, hasEditPermissions]
  );

  const handleEventSelect = React.useCallback(
    (arg: EventClickArg) => {
      const event = events.get(arg.event.id);
      
      if (hasEditPermissions) {
        // For admin users, open edit dialog
        setCurrentEventId(arg.event.id);
      } else {
        // For non-admin users, open view-only dialog
        viewEventDialog.handleOpen({ event });
      }
    },
    [setCurrentEventId, events, hasEditPermissions, viewEventDialog]
  );

  const handleEventResize = React.useCallback(
    (arg: EventResizeDoneArg) => {
      if (!hasEditPermissions) {
        // For non-admin users, don't allow resizing
        return;
      }

      const { event } = arg;
      updateEvent(event.id, { allDay: event.allDay, start: event.start!, end: event.end! });
    },
    [updateEvent, hasEditPermissions]
  );

  const handleEventDrop = React.useCallback(
    (arg: EventDropArg) => {
      if (!hasEditPermissions) {
        // For non-admin users, don't allow dragging
        return;
      }

      const { event } = arg;
      updateEvent(event.id, { allDay: event.allDay, start: event.start!, end: event.end! });
    },
    [updateEvent, hasEditPermissions]
  );

  // Transform events for FullCalendar
  const calendarEvents = React.useMemo(() => {
    return Array.from(events.values()).map((event) => ({
      id: event.id,
      title: event.title,
      start: event.start,
      end: event.end,
      allDay: event.allDay,
      backgroundColor: 'transparent', // Let our custom component handle the styling
      borderColor: 'transparent',
      textColor: 'transparent', // Hide default text since we're using custom rendering
      extendedProps: {
        description: event.description,
        priority: event.priority,
        color: event.color,
        type: event.type,
      },
    }));
  }, [events]);

  return (
    <React.Fragment>
      <Stack spacing={4}>
        <Toolbar 
          date={date} 
          onAdd={handleAdd} 
          onViewChange={handleViewChange} 
          view={view}
        />
        <Card sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: '800px' }}>
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                <div>Loading calendar events...</div>
              </Box>
            ) : isError ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                <div>Error loading calendar events. Please try again.</div>
              </Box>
            ) : (
              <Calendar
                allDayMaintainDuration
                dayMaxEventRows={3}
                droppable={hasEditPermissions}
                editable={hasEditPermissions}
                eventClick={handleEventSelect}
                eventContent={EventContent}
                eventDisplay="block"
                eventDrop={handleEventDrop}
                eventMinHeight={25}
                eventResizableFromStart={hasEditPermissions}
                eventResize={handleEventResize}
                events={calendarEvents}
                headerToolbar={false}
                height={800}
                initialDate={date}
                initialView={view}
                plugins={plugins}
                ref={calendarRef}
                rerenderDelay={10}
                select={hasEditPermissions ? handleRangeSelect : undefined}
                selectable={hasEditPermissions}
                weekends
              />
            )}
          </Box>
        </Card>
        <Stack direction="row" spacing={3} sx={{ justifyContent: 'center' }}>
          <IconButton onClick={handleDatePrev}>
            <CaretLeftIcon />
          </IconButton>
          <Button color="secondary" onClick={handleDateToday} variant="outlined">
            Today
          </Button>
          <IconButton onClick={handleDateNext}>
            <CaretRightIcon />
          </IconButton>
        </Stack>
      </Stack>
      
      {/* Create Event Dialog - Only for users with edit permissions */}
      {createDialog.open && hasEditPermissions ? (
        <EventDialog
          action="create"
          onClose={createDialog.handleClose}
          onCreate={(params) => {
            createEvent(params);
            createDialog.handleClose();
          }}
          open
          range={createDialog.data?.range}
        />
      ) : null}
      
      {/* Edit Event Dialog - Only for users with edit permissions */}
      {currentEvent && hasEditPermissions ? (
        <EventDialog
          action="update"
          event={currentEvent}
          onClose={() => {
            setCurrentEventId(undefined);
          }}
          onCreate={createEvent}
          onDelete={(eventId) => {
            setCurrentEventId(undefined);
            deleteEvent(eventId);
          }}
          onUpdate={(eventId, params) => {
            updateEvent(eventId, params);
            setCurrentEventId(undefined);
          }}
          open
        />
      ) : null}
      
      {/* View Event Dialog - For non-admin users */}
      {viewEventDialog.open ? (
        <ViewEventDialog
          event={viewEventDialog.data?.event}
          onClose={viewEventDialog.handleClose}
          open={viewEventDialog.open}
        />
      ) : null}
    </React.Fragment>
  );
}
